import {boundMethod} from "autobind-decorator";
import {parseISO} from "date-fns";
import React from "react";
import {injectIntl, IntlShape} from "react-intl";

import {ESopLicenses, IFeatureLicenseInfo} from "@/components/license/models";
import networkDevices from "@/services/network-devices";
import {EServerId} from "@shared/models";
import {EDeviceClasses, ILicensedDevice} from "@toolbox/models";
import {ILocalizedText} from "@translate/models";

import {getPrintDate} from "@toolbox/design/Time";
import {
    deviceName2Device,
    hasSingleChannel,
} from "@toolbox/functions/device-check";
import {
    getSopTooltipInfo,
    groupByDate,
} from "@toolbox/functions/license-functions";
import T, {intl2Str} from "@translate/T";
import LicenseFeatures from "./LicenseFeatures";

export function sopLicenseDisplayName(license: ESopLicenses): ILocalizedText {
    switch (license) {
        case ESopLicenses.Fast:
            return (intl) => intl2Str(intl, "Interval below 5 seconds");

        case ESopLicenses.Long:
            return (intl) => intl2Str(intl, "Duration above 42 hours");

        case ESopLicenses.Magnet:
            return (intl) => intl2Str(intl, "Magnet cell holders");

        case ESopLicenses.Template:
            return (intl) => intl2Str(intl, "Templates");

        case ESopLicenses.Unlimited:
            return (intl) =>
                intl2Str(
                    intl,
                    "Unlimited maximum interval, number of profiles and duration",
                );

        case ESopLicenses.TemperatureRamp:
            return (intl) => intl2Str(intl, "Temperature ramp");

        case ESopLicenses.SectorShapedCells:
            return (intl) => intl2Str(intl, "Sector Shaped Cell Types (SSC)");

        case ESopLicenses.ACsvExport:
            return (intl) => intl2Str(intl, "Automatic export");

        case ESopLicenses.FracUnlimitedDuration:
            return (intl) => intl2Str(intl, "Unlimited SOP duration");

        case ESopLicenses.FracMultiCycleStressCurveSOP:
            return (intl) => intl2Str(intl, "Multi cycle stress curve");
    }
}

function getLicensedSops(
    serverId: EServerId,
    _: ESopLicenses[],
    devices: ILicensedDevice[],
): ESopLicenses[] {
    const sops: ESopLicenses[] = [];
    const deviceClasses: EDeviceClasses[] = [
        ...new Set(devices.map((x) => deviceName2Device(x.deviceClass))),
    ];

    switch (serverId) {
        case EServerId.Centrifuge:
            if (deviceClasses.some((x) => !hasSingleChannel(x))) {
                sops.push(ESopLicenses.Fast, ESopLicenses.Long);
            }

            sops.push(
                ESopLicenses.ACsvExport,
                ESopLicenses.Magnet,
                ESopLicenses.SectorShapedCells,
                ESopLicenses.TemperatureRamp,
                ESopLicenses.Template,
                ESopLicenses.Unlimited,
            );
            break;

        case EServerId.Fracture:
            sops.push(
                ESopLicenses.FracMultiCycleStressCurveSOP,
                ESopLicenses.FracUnlimitedDuration,
            );
            break;

        case EServerId.Spoc:
            sops.push(ESopLicenses.Template);
            break;
    }

    return sops;
}

function isNonBuyable(item: ESopLicenses) {
    return [ESopLicenses.Unlimited].includes(item);
}

interface ISopFeaturesProps {
    intl: IntlShape;
    values: ESopLicenses[];
    devices: ILicensedDevice[];
    sopsLicense?: Record<string, IFeatureLicenseInfo[]>;
}

class SopFeatures extends React.PureComponent<ISopFeaturesProps> {
    public render() {
        const {values, devices} = this.props;

        return (
            <LicenseFeatures
                id="licensed-sop-features"
                title={<T>SOP features</T>}
                values={values}
                devices={devices}
                missing="missingSopLicenses"
                getLicensed={getLicensedSops}
                getDisplayName={sopLicenseDisplayName}
                isNonBuyable={isNonBuyable}
                getTooltip={this.getTooltip}
            />
        );
    }

    @boundMethod
    private getTooltip(current: ESopLicenses) {
        const {intl, sopsLicense} = this.props;

        if (!sopsLicense) {
            return;
        }

        const licenseData = getSopTooltipInfo(current, sopsLicense);
        const licenseGroupedData = Object.entries(groupByDate(licenseData));

        if (licenseGroupedData.length === 0) {
            return;
        }

        return (
            <ul>
                {licenseGroupedData.map(([date, items]) => {
                    const devices = items.map((item) =>
                        networkDevices.getDeviceName(
                            intl,
                            item.device as ILicensedDevice,
                        ),
                    );
                    return (
                        <li key={date + "-sops"}>
                            <strong>
                                {getPrintDate(intl, parseISO(date))}:
                            </strong>{" "}
                            {items.length > 0 && (
                                <div>
                                    <T>Licensed devices:</T>{" "}
                                    {devices.join(", ")}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    }
}

export default injectIntl(SopFeatures);
